import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext({ theme: 'light', toggle: () => {} });

const STORAGE_KEY = 'matbakhi_theme';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
      if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch (e) { console.warn('[Theme] read storage failed', e); }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try { localStorage.setItem(STORAGE_KEY, theme); } catch (e) { console.warn('[Theme] persist failed', e); }
    // Sync native Android/iOS status bar with the current theme so the
    // system clock + icons stay readable on both light and dark backgrounds.
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        if (theme === 'dark') {
          await StatusBar.setStyle({ style: Style.Light });           // light (white) icons for dark bg
          await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
        } else {
          await StatusBar.setStyle({ style: Style.Dark });            // dark icons for light bg
          await StatusBar.setBackgroundColor({ color: '#F9F6F0' });
        }
      } catch (e) { /* status bar plugin not available on web */ }
    })();
  }, [theme]);

  const toggle = useCallback(() => setTheme(t => (t === 'dark' ? 'light' : 'dark')), []);

  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
